/**
 * Combo Comparison Component
 * Displays a comparison table of all combo options ranked by cost
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, ListOrdered, AlertCircle } from 'lucide-react';
import type { ComboRanking } from '../types';
import { MILEAGE_PROGRAM_LABELS } from '../types';
import {
  formatCurrency,
  formatPercentage,
  formatMiles,
} from '../utils/calculations';

interface ComboComparisonProps {
  ranking: ComboRanking;
  onSelectCombo?: (comboId: string) => void;
  selectedComboId?: string;
}

export function ComboComparison({
  ranking,
  onSelectCombo,
  selectedComboId,
}: ComboComparisonProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Show top 5 by default, or all if expanded
  const displayCombos = isExpanded ? ranking.combos : ranking.combos.slice(0, 5);
  const hasMore = ranking.combos.length > 5;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-zinc-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-500/10 text-purple-500">
              <ListOrdered className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-100">
                Ranking de Combinações
              </h3>
              <p className="text-sm text-zinc-400">
                {ranking.combos.length} {ranking.combos.length === 1 ? 'combinação' : 'combinações'}
                {' '}• {ranking.hasViableOption ? 'Opções viáveis disponíveis' : 'Nenhuma opção viável'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-zinc-800/50">
        <div className="bg-zinc-900/40 p-4 text-center">
          <p className="text-xs text-zinc-400 mb-1">Total</p>
          <p className="text-lg font-bold text-zinc-100">{ranking.combos.length}</p>
        </div>
        <div className="bg-zinc-900/40 p-4 text-center">
          <p className="text-xs text-zinc-400 mb-1">Viáveis</p>
          <p className="text-lg font-bold text-emerald-400">
            {ranking.combos.filter(c => c.isViable).length}
          </p>
        </div>
        <div className="bg-zinc-900/40 p-4 text-center">
          <p className="text-xs text-zinc-400 mb-1">Inviáveis</p>
          <p className="text-lg font-bold text-red-400">
            {ranking.combos.filter(c => !c.isViable).length}
          </p>
        </div>
      </div>

      {/* Combo List */}
      <div className="divide-y divide-zinc-800/50">
        {displayCombos.map((combo) => (
          <div
            key={combo.id}
            className={`p-4 transition-all ${
              selectedComboId === combo.id
                ? 'bg-[#B05D41]/10 border-l-2 border-[#B05D41]'
                : 'hover:bg-zinc-800/30'
            } ${onSelectCombo ? 'cursor-pointer' : ''}`}
            onClick={() => onSelectCombo?.(combo.id)}
          >
            <div className="flex items-start gap-4">
              {/* Rank Badge */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                combo.rank === 1
                  ? 'bg-gradient-to-br from-[#B05D41] to-[#8B4A33] text-white'
                  : combo.rank === 2
                  ? 'bg-zinc-700 text-zinc-200'
                  : combo.rank === 3
                  ? 'bg-zinc-800 text-zinc-300'
                  : 'bg-zinc-900 text-zinc-400'
              }`}>
                #{combo.rank}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  {/* Programs */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-zinc-500">Ida:</span>
                        <span className="text-sm font-medium text-[#B05D41]">
                          {MILEAGE_PROGRAM_LABELS[combo.outbound.program]}
                        </span>
                        <span className="text-xs text-zinc-500">
                          ({formatMiles(combo.outbound.miles)})
                        </span>
                      </div>
                      {combo.return && (
                        <>
                          <span className="text-zinc-600">→</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-zinc-500">Volta:</span>
                            <span className="text-sm font-medium text-blue-400">
                              {MILEAGE_PROGRAM_LABELS[combo.return.program]}
                            </span>
                            <span className="text-xs text-zinc-500">
                              ({formatMiles(combo.return.miles)})
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Costs */}
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <div>
                        <span className="text-zinc-500">Custo: </span>
                        <span className="text-zinc-300 font-medium">
                          {formatCurrency(combo.totalCost)}
                        </span>
                      </div>
                      {combo.isViable ? (
                        <div className="flex items-center gap-1 text-emerald-400">
                          <span>Economia:</span>
                          <span className="font-medium">
                            {formatCurrency(combo.savings)} ({formatPercentage(combo.savingsPercentage)})
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-red-400">
                          <AlertCircle className="w-3 h-3" />
                          <span>Inviável</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Visual Indicator */}
                  <div className="flex items-center gap-2">
                    {combo.rank === 1 && (
                      <div className="px-2 py-1 bg-[#B05D41]/20 border border-[#B05D41]/30 rounded text-xs font-medium text-[#B05D41]">
                        Melhor
                      </div>
                    )}
                    {!combo.isViable && (
                      <div className="px-2 py-1 bg-red-500/20 border border-red-500/30 rounded text-xs font-medium text-red-400">
                        Inviável
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expand/Collapse Button */}
      {hasMore && (
        <div className="p-4 border-t border-zinc-800/50">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Mostrar menos
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Ver todas as {ranking.combos.length} combinações
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
